import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useMutation } from "react-query";

import { theme } from "../../../theme";

import LoadingBox from "../../atoms/LoadingBox";

import { SERVICE_KEY } from "../../../shared/constants";
import ExplainWrapper from "../ExplainWrapper";
import { ValidationType } from "../../atoms/Explain/types";
import Explain from "../../atoms/Explain";

import { uploadImage } from "./apis";
import { Media } from "../../../model/Media";
import Button from "../../atoms/Button";
import ImageTextBox from "./components/ImageTextBox";

interface Props {
  imageList?: Media[];
  max?: number;
  fileMaxMbLength?: number;
  folder: string;
  multiple?: boolean;
  inputKey?: string;
  onUpload: (e: Media[]) => void;
  onRemove?: (e: Media[]) => void;
  imageExplain?: string[];
  errorExplain?: string;
  placeholder?: string;
  disabled?: boolean;
}
const ImageTextUploader: React.FC<Props> = ({
  imageList,
  max,
  folder,
  inputKey,
  fileMaxMbLength = 1024 * 1024 * 3,
  multiple,
  onUpload,
  onRemove,
  imageExplain,
  errorExplain,
  placeholder,
  disabled,
}) => {
  const ImageUploadMutation = useMutation(uploadImage, {
    onSuccess: (res) => {
      const newResult = res;
      onUpload([...mediaList, ...newResult]);
      setMediaList([...mediaList, ...newResult]);
      setPreImageNum(0);
    },
    onError: () => {
      onUpload([]);
      setPreImageNum(0);

      setMediaList([]);
      setStatus({
        status: "error",
        explain: `파일을 업로드하는데 문제가 생겼습니다.`,
      });
    },
  });
  const inputRef = useRef<any>(null);
  const [preImageNum, setPreImageNum] = useState<number>(0);
  const [status, setStatus] = useState<ValidationType>({
    status: errorExplain ? "error" : undefined,
    explain: errorExplain,
  });

  const [mediaList, setMediaList] = useState<Media[]>(imageList || []);

  const onChange = (e: any) => {
    const { files } = e.target;
    const newData = new FormData();
    newData.append("service", SERVICE_KEY);
    newData.append("folder", folder);
    let newPreImageNum = preImageNum;
    const fileEntries = Object.values<File>(files);
    let isError = false;
    setStatus({ status: undefined, explain: undefined });
    for (let i = 0; i < fileEntries.length; i += 1) {
      if (fileEntries[i].size < fileMaxMbLength)
        if (max) {
          if (mediaList.length + (newPreImageNum + 1) <= max) {
            newData.append("image", fileEntries[i]);
            newPreImageNum += 1;
          }
        } else {
          newData.append("image", fileEntries[i]);
          newPreImageNum += 1;
        }
      else {
        isError = true;
        setStatus({
          status: "error",
          explain: `파일 사이즈가 제한을 넘어섭니다.`,
        });
      }
    }
    if (inputRef.current) inputRef.current.value = "";
    setPreImageNum(newPreImageNum);
    if (isError !== true) ImageUploadMutation.mutate(newData);
  };
  const renderLoadingBox = () => {
    const boxes = [];
    for (let i = 0; i < preImageNum; i += 1) {
      boxes.push(<LoadingBox key={`preImage${i}`} />);
    }
    return boxes;
  };

  const onRemoveBox = (index: number) => {
    const newMediaList = [...mediaList];
    newMediaList.splice(index, 1);
    setMediaList(newMediaList);

    if (onRemove) onRemove(newMediaList);
  };
  useEffect(() => {
    setStatus({
      status: errorExplain ? "error" : undefined,
      explain: errorExplain,
    });
  }, [errorExplain]);
  useEffect(() => {
    if (imageList) {
      setMediaList(imageList);
    }
  }, [imageList, preImageNum]);
  return (
    <>
      <ExplainWrapper status={status}>
        <UploaderWrapper status={status.status}>
          <ContentWrapper>
            {mediaList.length > 0
              ? mediaList?.map((value, index) => {
                  if (value) {
                    const { id, fileName = "" } = value;
                    return (
                      <ImageTextBox
                        onRemove={
                          disabled ? undefined : () => onRemoveBox(index)
                        }
                        key={id || 0 + index + fileName}
                        imgName={fileName}
                      />
                    );
                  }
                  return null;
                })
              : placeholder || "파일을 선택해주세요"}
          </ContentWrapper>
          <StyledLabel htmlFor={`preview_uploader${inputKey}`}>
            <Button
              disabled={
                disabled ||
                mediaList.length === max ||
                renderLoadingBox().length > 0
              }
              htmlType="button"
              id={`preview_uploader${inputKey}`}
              ghost
              size="large"
              onClick={() => {
                inputRef.current.click();
              }}
            >
              이미지 업로드
            </Button>
          </StyledLabel>

          <StyledUploader
            id={`preview_uploader${inputKey}`}
            name="preview_uploader"
            type="file"
            ref={inputRef}
            accept="image/jepg,image/png,image/gif"
            multiple={multiple}
            onChange={onChange}
          />
        </UploaderWrapper>

        {imageExplain?.map((val, index) => {
          return <Explain key={index} status="question" explain={val} />;
        })}
      </ExplainWrapper>
    </>
  );
};
export default ImageTextUploader;
const StyledUploader = styled.input`
  visibility: hidden;
  width: 0px;
  background-color: black;
  display: flex;
  justify-content: flex-start;
  overflow-y: hidden;
`;
const StyledLabel = styled.label``;

const UploaderWrapper = styled.div<{ status?: string }>`
  display: flex;
  overflow-x: auto;
  overflow-y: hidden;
  justify-content: flex-start;
  width: 100%;
  border-bottom: 1px solid
    ${({ status }) =>
      status === "error" ? theme.colors.red3 : theme.colors.gray6};
  padding-bottom: 8px;
`;
const ContentWrapper = styled.div`
  flex: 1 0 0px;
  flex-grow: 1;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  overflow-x: auto;
  color: ${theme.colors.gray3};
  line-height: 24px;
`;
